// Educational Content Service for backend communication
import { api } from './api';

// Block Breaker Types
export type BlockBreakerCategory = 'physical' | 'mental' | 'social' | 'environmental' | 'creative';

export interface BlockBreaker {
  id: string;
  title: string;
  description: string;
  category: BlockBreakerCategory;
  estimatedMinutes: number;
  icon: string;
}

export interface BlockBreakersResponse {
  breakers: BlockBreaker[];
}

// Educational Article Types
export type ArticleCategory = 'science' | 'inspiration' | 'practical';

export interface EducationalArticle {
  id: string;
  title: string;
  summary: string;
  category: ArticleCategory;
  readTimeMinutes: number;
  content?: string;
}

export interface ArticlesResponse {
  articles: EducationalArticle[];
}

// Creative Challenge Types
export type ChallengeDifficulty = 'easy' | 'medium' | 'hard';

export interface CreativeChallenge {
  id: string;
  title: string;
  description: string;
  duration: string;
  difficulty: ChallengeDifficulty;
  participantCount: number;
  isPremium: boolean;
}

export interface ChallengesResponse {
  challenges: CreativeChallenge[];
}

// Creative Block Tips Types
export interface BlockTip {
  title: string;
  description: string;
  icon: string;
}

export interface CreativeBlockTips {
  title: string;
  introduction: string;
  tips: BlockTip[];
  emergencyActions: string[];
}

// Morning Pages Guide Types
export interface GuideSection {
  title: string;
  points: string[];
}

export interface MorningPagesGuide {
  title: string;
  introduction: string;
  sections: GuideSection[];
  quote?: {
    text: string;
    author: string;
  };
}

// Mood Resources Types
export interface ContentLink {
  title: string;
  url: string;
}

export interface MoodResources {
  mood: string;
  title: string;
  message: string;
  suggestions: string[];
  relatedContent: ContentLink[];
}

// Educational Content API
export const educationalContentApi = {
  // Get block breaker suggestions
  getBlockBreakers: (category?: string, count: number = 4, language: string = 'en'): Promise<BlockBreakersResponse> => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    params.append('count', count.toString());
    params.append('language', language);
    return api.get<BlockBreakersResponse>(`/educationalcontent/block-breakers?${params.toString()}`);
  },

  // Get educational articles
  getArticles: (category?: string, language: string = 'en'): Promise<ArticlesResponse> => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    params.append('language', language);
    return api.get<ArticlesResponse>(`/educationalcontent/articles?${params.toString()}`);
  },

  // Get a specific article by ID
  getArticleById: (id: string, language: string = 'en'): Promise<EducationalArticle> => {
    return api.get<EducationalArticle>(`/educationalcontent/articles/${id}?language=${language}`);
  },

  // Get creative challenges
  getChallenges: (difficulty?: string, language: string = 'en'): Promise<ChallengesResponse> => {
    const params = new URLSearchParams();
    if (difficulty) params.append('difficulty', difficulty);
    params.append('language', language);
    return api.get<ChallengesResponse>(`/educationalcontent/challenges?${params.toString()}`);
  },

  // Get creative block tips
  getCreativeBlockTips: (language: string = 'en'): Promise<CreativeBlockTips> => {
    return api.get<CreativeBlockTips>(`/educationalcontent/creative-block-tips?language=${language}`);
  },

  // Get morning pages guide
  getMorningPagesGuide: (language: string = 'en'): Promise<MorningPagesGuide> => {
    return api.get<MorningPagesGuide>(`/educationalcontent/morning-pages-guide?language=${language}`);
  },

  // Get mood-specific resources
  getMoodResources: (mood: string, language: string = 'en'): Promise<MoodResources> => {
    return api.get<MoodResources>(`/educationalcontent/mood-resources/${mood}?language=${language}`);
  },
};

export default educationalContentApi;
